const React = require('react');
const { useSelector, useDispatch } = require('react-redux');
const { useState, useMemo } = React;
const IrrigationRecommendationCard = require('./IrrigationRecommendationCard');
require('./RecommendationTileWrapper.css');
const $ = require('jquery');
const { useTranslation } = require('react-i18next');
const {
  parseDateInput,
  FromDateToStringFormat,
  getThisWeekIndex,
  getWeekType,
  weeklyPlannerModes,
} = require('@managers/planningManager');
const RecommendationFeedback = require('./RecommendationFeedback');
const NoPlanningMessage = require('./NoPlanningMessage');
const {
  update_field_recommendation_show_rate_line,
} = require('@actions/distributionActions');

const RecommendationTileWrapper = () => {
  const dispatch = useDispatch();

  // Get current field and recommendations from Redux
  const selectedFieldRedux = useSelector(
    (state) => state.distribution_data.selected_entities.field,
  );
  const recommendationsByFieldRedux = useSelector(
    (state) =>
      state.distribution_data.farming_unit_recommendations.recommendations,
  );

  const { t } = useTranslation();

  // Defensive: If no field or no recommendations, show nothing
  if (!selectedFieldRedux || !recommendationsByFieldRedux) {
    return null;
  }

  // Get the recommendations array for the current field
  const recArr =
    recommendationsByFieldRedux[selectedFieldRedux.id]?.recommendations || [];

  const showRateLineFromRedux =
    recommendationsByFieldRedux[selectedFieldRedux.id]?.show_rate_line;

  const recommendationUnits =
    recommendationsByFieldRedux[selectedFieldRedux.id]?.units || 'mm';

  // Transform array to object keyed by week (local_midnight_week_start)
  const recByWeek = useMemo(() => {
    return recArr.reduce((acc, rec) => {
      const weekKey = rec?.local_midnight_week_start;
      acc[weekKey] = rec;
      return acc;
    }, {});
  }, [recArr]);

  // Get all week keys for this field, sorted chronologically
  const weekKeys = useMemo(() => Object.keys(recByWeek).sort(), [recByWeek]);
  // Compute all week start dates and determine 'this week' index
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thisWeekIdx = getThisWeekIndex(weekKeys, today);

  // Local state: which week is currently shown (default: 'this week')
  const [weekIdx, setWeekIdx] = useState(thisWeekIdx);

  // Ensure weekIdx is updated when thisWeekIdx changes (e.g., new data loads)
  React.useEffect(() => {
    setWeekIdx(thisWeekIdx);
  }, [thisWeekIdx]);

  // Get the current recommendation
  const weekKey = weekKeys[weekIdx];
  const rec = recByWeek[weekKey];

  // Compute weekType for the card header based on today/selected week
  let weekType = getWeekType(weekIdx, thisWeekIdx);

  // Compute weekRange: (start - end)
  const startDateRaw = weekKeys[weekIdx];
  const startDateObj = parseDateInput(startDateRaw);

  // End date is the day before the next week start, or add 6 days if last week
  let endDateObj;
  if (weekIdx < weekKeys.length - 1) {
    const nextStart = parseDateInput(weekKeys[weekIdx + 1]);
    endDateObj = new Date(nextStart);
    endDateObj.setDate(endDateObj.getDate() - 1);
  } else {
    // For the last week, calculate end date by adding 6 days to start date
    endDateObj = new Date(startDateObj);
    endDateObj.setDate(endDateObj.getDate() + 6);
  }
  const weekRange = `(${FromDateToStringFormat(startDateObj)} - ${FromDateToStringFormat(endDateObj)})`;

  const FeedbackHandler = () => {
    // Update Redux state to hide the feedback component after thanks message
    setTimeout(() => {
      dispatch(
        update_field_recommendation_show_rate_line(
          selectedFieldRedux.id,
          false,
        ),
      );
    }, 3500);
  };

  const plannerMode =
    selectedFieldRedux?.settings?.planner_settings?.mode ||
    weeklyPlannerModes.WEEKLY;

  const cardProps = {
    weekType,
    weekRange,
    irrigate: rec?.irrigations?.amount,
    frequency: rec?.irrigations?.events,
    dailyReturn: rec?.irrigations?.daily_return,
    onPrev: () => setWeekIdx((idx) => Math.max(0, idx - 1)),
    onNext: () => setWeekIdx((idx) => Math.min(weekKeys.length - 1, idx + 1)),
    isPrevDisabled: weekIdx === 0,
    isNextDisabled: weekIdx === weekKeys.length - 1,
    mode: plannerMode,
    user_length_unit: recommendationUnits,
  };

  // Tooltip IDs (unique per component instance)
  const tooltipID = 'irrigationRecInfoTooltip';
  const tooltipContainerID = 'irrigationRecInfoTooltipContainer';

  React.useEffect(() => {
    $(() => {
      $(`[data-toggle="tooltip"]`).tooltip({ offset: '0,6' });
    });
  }, []);

  return (
    <div className='recommendation-tile-wrapper'>
      <div className='recommendation-tile-card'>
        <div className='header' id={tooltipContainerID}>
          <span className='cardTitle'>{t('Irrigation recommendation')}</span>
          <img
            data-toggle='tooltip'
            id={tooltipID}
            data-container={`#${tooltipContainerID}`}
            onMouseOver={() => {
              $(`#${tooltipID}`).tooltip('show');
            }}
            data-placement='top'
            title={t('irrigation_recommendation_tooltip')}
            className='infoIcon'
            src={require('images/icons/info_icon.svg')}
          />
        </div>
        {(() => {
          if (cardProps.irrigate === undefined) {
            return (
              <NoPlanningMessage
                weekType={weekType}
                weekRange={weekRange}
                onPrev={cardProps.onPrev}
                onNext={cardProps.onNext}
                isPrevDisabled={cardProps.isPrevDisabled}
                isNextDisabled={cardProps.isNextDisabled}
                messageType='new_planner_no_recommendation_available_indication'
              />
            );
          }
          if (cardProps.irrigate === 0) {
            return (
              <NoPlanningMessage
                weekType={weekType}
                weekRange={weekRange}
                onPrev={cardProps.onPrev}
                onNext={cardProps.onNext}
                isPrevDisabled={cardProps.isPrevDisabled}
                isNextDisabled={cardProps.isNextDisabled}
                messageType='no_irrigation_planning_this_week'
              />
            );
          }
          return <IrrigationRecommendationCard {...cardProps} />;
        })()}
        <div
          className={`feedback-row-wrapper${weekType === 'this' && showRateLineFromRedux ? ' visible' : ''}`}
        >
          <RecommendationFeedback
            onChange={FeedbackHandler}
            t={t}
            recommendationAmount={rec?.irrigations?.amount}
            weekRange={weekRange}
            recommendationMode={plannerMode}
            fieldId={selectedFieldRedux?.id}
            dateGenerated={rec?.date_generated}
            localMidnightWeekStart={weekKey}
          />
        </div>
      </div>
    </div>
  );
};

module.exports = React.memo(RecommendationTileWrapper);
